import type { IFilesystem } from '@livx.cc/wcli/core';
import type { AgentTool, ToolContext } from './tools';
import type { ChatResponse } from './llm';
import { redactSecrets, CONFIG_FILE_RE } from './redact';

/**
 * Structured file tools — typed results straight from the VFS, no `bash` parsing.
 * These close the efficiency gap to Claude Code (its Grep/Glob/Write return one
 * structured result where our agent otherwise drives find/grep pipelines and re-reads).
 * Backend-agnostic: pure IFilesystem walks, so they run on Mem/Disk/IndexedDb alike.
 */

/** Throw the moment a run is cancelled, so a long file walk/scan bails instead of pegging the CPU
 *  uninterruptibly. dispatch() catches the throw → the run loop sees `aborted` and ends the turn. */
function ckAbort(signal?: AbortSignal): void {
  if (signal?.aborted) throw new Error('aborted');
}

/**
 * Directory names never worth walking. Pruned by NAME at every level (not by path), because the
 * cost is where they nest: a real-FS root of a JS deployment is mostly `node_modules`, and on a
 * measured 412k-file app root a single `Glob` spent 31s almost entirely inside it.
 */
export const DEFAULT_SKIP_DIRS = ['node_modules', '.git', 'dist', '.tmp'];

/** Depth below a root at which the walk stops. Deep enough for any real source tree; a bound at all
 *  is what turns a symlink cycle from a hang into a truncated result. */
export const DEFAULT_MAX_DEPTH = 24;

/** How the file-scanning tools (Glob/Grep) decide WHAT to walk. */
export interface FileScanOptions {
  /** Roots to walk when a call names no `path`. Default: `[fs.getCwd()]`, i.e. today's behaviour.
   *  A host with its data outside the cwd passes both, so a search can reach it at all. */
  roots?: string[];
  /** Directory names pruned during the walk. Default: {@link DEFAULT_SKIP_DIRS}. */
  skipDirs?: string[];
  /** Max depth below each root. Default: {@link DEFAULT_MAX_DEPTH}. */
  maxDepth?: number;
}

interface WalkState {
  skip: Set<string>;
  maxDepth: number;
  /** Real paths already visited — the cycle/duplication guard. A `vendor` symlink pointing back
   *  into the tree (or `node_modules/.bin`) otherwise re-walks the same subtree repeatedly.
   *  PER ROOT, not shared: with one shared set a second root that is a symlink to (or into) the
   *  first is "already seen" and silently yields nothing — the caller asked for a place and got an
   *  empty answer, which is the absent-vs-unreachable confusion this file is trying to kill. */
  seen: Set<string>;
  /** Directory names actually pruned, and depth cuts actually made — reported to the caller, never
   *  swallowed: a walk that refused to look somewhere must say so, or `(no matches)` is a lie. */
  prunedDirs: Set<string>;
  depthCuts: number;
}

/** `canonicalPath` (symlinks resolved) is an optional capability on disk-backed filesystems; on
 *  backends without it the VFS path IS the identity, so it is its own canonical form. Note it must
 *  be the symlink-resolving one: `realPath` is path arithmetic and reports a link and its target as
 *  two different places, which cannot break a cycle. */
function realOf(fs: IFilesystem, p: string): string {
  const rp = (fs as { canonicalPath?: (path: string) => string }).canonicalPath;
  if (typeof rp !== 'function') return p;
  try { return rp.call(fs, p) || p; } catch { return p; }
}

/** Recursively list every file path under `dir` (VFS-absolute), depth-first, sorted. Cancellable:
 *  a cancelled run throws between entries so a huge tree walk (real-FS disk mode) doesn't wedge.
 *  Prunes {@link DEFAULT_SKIP_DIRS}, bounds depth, and never enters the same real dir twice. */
async function walkFiles(fs: IFilesystem, dir: string, signal?: AbortSignal, out: string[] = [], st?: WalkState, depth = 0): Promise<string[]> {
  const state: WalkState = st ?? { skip: new Set(DEFAULT_SKIP_DIRS), maxDepth: DEFAULT_MAX_DEPTH, seen: new Set(), prunedDirs: new Set(), depthCuts: 0 };
  if (depth > state.maxDepth) { state.depthCuts++; return out; }
  const real = realOf(fs, dir);
  if (state.seen.has(real)) return out;
  state.seen.add(real);
  let entries: string[];
  try { entries = await fs.readDir(dir); } catch { return out; }
  for (const name of entries.sort()) {
    ckAbort(signal);
    if (state.skip.has(name)) { state.prunedDirs.add(name); continue; }
    const p = dir === '/' ? `/${name}` : `${dir}/${name}`;
    if (await fs.isDirectory(p)) await walkFiles(fs, p, signal, out, state, depth + 1);
    else out.push(p);
  }
  return out;
}

/** The roots a call actually walks: an explicit `path` wins, else the configured roots, else the
 *  cwd. Nested roots are collapsed — walking `/a` and `/a/b` would double-report everything in b. */
function scanRoots(fs: IFilesystem, path: unknown, opts?: FileScanOptions): string[] {
  const asked = path == null ? [] : (Array.isArray(path) ? path : [path]).map((p) => String(p)).filter(Boolean);
  const roots = (asked.length ? asked : (opts?.roots?.length ? opts.roots : [fsCwd(fs)])).map((r) => (r.length > 1 && r.endsWith('/') ? r.slice(0, -1) : r));
  return [...new Set(roots)].filter((r, _i, all) => !all.some((other) => other !== r && r.startsWith(`${other}/`)));
}

/** What a walk refused to look at — appended to results so a pruned/truncated search never reads
 *  as an exhaustive one. Mirrors Grep's existing "skipped N unreadable files" note. */
function scanNote(states: WalkState[]): string {
  const pruned = [...new Set(states.flatMap((st) => [...st.prunedDirs]))].sort();
  const cuts = states.reduce((n, st) => n + st.depthCuts, 0);
  const parts: string[] = [];
  if (pruned.length) parts.push(`skipped ${pruned.join(', ')} (pass skipDirs to include them)`);
  if (cuts) parts.push(`stopped at depth ${states[0]?.maxDepth} in ${cuts} place${cuts === 1 ? '' : 's'} (pass maxDepth to go deeper)`);
  return parts.length ? `\n[scan: ${parts.join('; ')}]` : '';
}

/** Walk every root once, with the configured prune/depth/cycle guards. Per-call `skipDirs`/`maxDepth`
 *  override the construction-time ones, so a consumer of the default singletons is never stuck with
 *  a prune it needs to see past (`Glob '**\/dist/*'`, a `.tmp/` workspace). */
async function walkRoots(
  fs: IFilesystem, roots: string[], signal: AbortSignal | undefined, opts?: FileScanOptions, call?: FileScanOptions,
): Promise<{ files: string[]; note: string }> {
  const skip = new Set(call?.skipDirs ?? opts?.skipDirs ?? DEFAULT_SKIP_DIRS);
  const maxDepth = call?.maxDepth ?? opts?.maxDepth ?? DEFAULT_MAX_DEPTH;
  const out: string[] = [];
  const states: WalkState[] = [];
  for (const root of roots) {
    // A fresh `seen` per root: cross-root dedupe belongs to the RESULT (callers Set-dedupe), not to
    // the walk, where it silently erases a whole root that aliases another.
    const state: WalkState = { skip, maxDepth, seen: new Set(), prunedDirs: new Set(), depthCuts: 0 };
    states.push(state);
    await walkFiles(fs, root, signal, out, state, 0);
  }
  return { files: out, note: scanNote(states) };
}

/** Per-call scan overrides, parsed from tool args. */
function callScan(args: { skipDirs?: unknown; maxDepth?: unknown }): FileScanOptions {
  const sd = args.skipDirs;
  return {
    skipDirs: Array.isArray(sd) ? sd.map(String) : typeof sd === 'string' ? sd.split(',').map((x) => x.trim()).filter(Boolean) : undefined,
    maxDepth: args.maxDepth == null ? undefined : Number(args.maxDepth),
  };
}

const SCAN_PARAMS = {
  path: { type: 'string', description: 'optional directory (or list) to search instead of the default roots; absolute VFS path' },
  skipDirs: { type: 'string', description: `comma-separated directory names to prune (default: ${DEFAULT_SKIP_DIRS.join(', ')}); pass an empty string to prune nothing` },
  maxDepth: { type: 'number', description: `max directory depth below each root (default ${DEFAULT_MAX_DEPTH})` },
} as const;

/** Anchor a relative glob at a specific root (an absolute pattern anchors to itself). Per-ROOT, not
 *  per-cwd: with two roots, anchoring `**\/*.md` at the cwd alone would silently match nothing in
 *  the other one — the "search can't reach it, so it reports absent" failure this all exists for. */
function anchoredGlobAt(root: string, glob: string): RegExp {
  if (glob.startsWith('/')) return globToRegExp(glob);
  return globToRegExp(`${root === '/' ? '' : root}/${glob}`);
}

/** A path/scope matcher over several roots: matches if the pattern matches when anchored at ANY. */
function globMatcher(roots: string[], glob: string): (p: string) => boolean {
  const res = roots.map((r) => anchoredGlobAt(r, glob));
  return (p: string) => res.some((re) => re.test(p));
}

/**
 * Translate a glob (`**`, `*`, `?`) into an anchored RegExp over VFS-absolute paths.
 * `caseInsensitive` is used by the JailedFilesystem denylist so `/.ENV` can't slip past
 * a `.env` rule (file-matching tools keep the default case-sensitive behavior).
 */
export function globToRegExp(glob: string, caseInsensitive = false): RegExp {
  const g = glob.startsWith('/') ? glob : `/${glob}`;
  let re = '';
  for (let i = 0; i < g.length; i++) {
    const c = g[i];
    if (c === '*') {
      if (g[i + 1] === '*') { re += '.*'; i++; if (g[i + 1] === '/') i++; }
      else re += '[^/]*';
    } else if (c === '?') re += '[^/]';
    else re += c.replace(/[.+^${}()|[\]\\]/g, '\\$&');
  }
  return new RegExp(`^${re}$`, caseInsensitive ? 'i' : '');
}

/** The dir structured tools walk/anchor at — the FS's working dir. Keeps Glob/Grep/RepoMap scoped
 *  to the project (or, in CC-parity disk mode where root '/' is the real machine, the launch dir)
 *  instead of crawling the whole filesystem. Backward-compatible: Mem/jailed-at-cwd backends report '/'. */
const fsCwd = (fs: IFilesystem): string => fs.getCwd();

/** Compile a glob, resolving a relative pattern under the FS cwd (mirrors a shell's cwd-relative globs).
 *  Absolute patterns are used as-is. With cwd '/' this is identical to the old `/`-anchored behavior. */
function anchoredGlob(fs: IFilesystem, glob: string): RegExp {
  const cwd = fsCwd(fs);
  const base = cwd === '/' ? '' : cwd;
  return globToRegExp(glob.startsWith('/') ? glob : `${base}/${glob}`);
}

/** List paths matching a glob, sorted — the structured alternative to `find`. */
export function makeGlobTool(opts?: FileScanOptions): AgentTool {
  return {
    name: 'Glob',
    description:
      'Find files by glob pattern (e.g. "**/*.ts", "src/**/*.test.ts"). Returns sorted paths, one per line. ' +
      'Space-separated patterns combine; `!`-prefix excludes (e.g. "**/*.ts !**/*.test.ts"). Optional `path` to search a specific directory. Prefer over `bash find` for locating files — one call, structured output.',
    parameters: {
      type: 'object',
      required: ['pattern'],
      properties: {
        pattern: { type: 'string', description: 'glob pattern(s); ** matches across directories; prefix a pattern with ! to exclude' },
        ...SCAN_PARAMS,
      },
    },
    async run({ pattern, path, skipDirs, maxDepth }, ctx) {
      const roots = scanRoots(ctx.fs, path, opts);
      const pats = String(pattern ?? '').trim().split(/\s+/).filter(Boolean);
      const include = pats.filter((p) => !p.startsWith('!')).map((p) => globMatcher(roots, p));
      const exclude = pats.filter((p) => p.startsWith('!')).map((p) => globMatcher(roots, p.slice(1)));
      const includes = include.length ? include : [globMatcher(roots, '**')]; // only-excludes → start from everything
      const { files, note } = await walkRoots(ctx.fs, roots, ctx.signal, opts, callScan({ skipDirs, maxDepth }));
      const hits = files.filter((p) => includes.some((m) => m(p)) && !exclude.some((m) => m(p)));
      return (hits.length ? [...new Set(hits)].sort().join('\n') : '(no matches)') + note;
    },
  };
}

/** Search file contents by regex, returning typed `path:line:text` hits with optional context. */
export function makeGrepTool(opts?: FileScanOptions): AgentTool {
  return {
    name: 'Grep',
    description:
      'Search file contents by regex. Returns `path:line: text` hits. Optional `glob` to scope files, `path` to search a specific directory, `context` for surrounding lines, `filesOnly` for matching paths only. Prefer over `bash grep` for file content search — structured results, no re-parse needed. Use `bash` instead for running commands, tests, or piped workflows.',
    parameters: {
      type: 'object',
      required: ['pattern'],
      properties: {
        pattern: { type: 'string', description: 'JS regular expression' },
        glob: { type: 'string', description: 'optional file glob to restrict the search' },
        ...SCAN_PARAMS,
        context: { type: 'number', description: 'lines of context before/after each hit' },
        filesOnly: { type: 'boolean', description: 'only list matching file paths' },
      },
    },
    async run({ pattern, glob, path, skipDirs, maxDepth, context, filesOnly }, ctx) {
      let re: RegExp;
      try { re = new RegExp(String(pattern ?? '')); } catch (e) { throw new Error(`invalid regex: ${String(e)}`); }
      const roots = scanRoots(ctx.fs, path, opts);
      const scope = glob ? globMatcher(roots, String(glob)) : null;
      const walked = await walkRoots(ctx.fs, roots, ctx.signal, opts, callScan({ skipDirs, maxDepth }));
      const files = [...new Set(walked.files)].sort().filter((p) => !scope || scope(p));
      const ctxN = Math.max(0, Number(context ?? 0));
      const out: string[] = [];
      const matched: string[] = [];
      let skipped = 0; // unreadable files (permissions, races) — surfaced, not silently dropped
      for (const path of files) {
        ckAbort(ctx.signal); // cancellable per-file: a wide grep over a big tree stops on Esc, not after
        let content: string;
        try { content = await ctx.fs.readFile(path); } catch { skipped++; continue; }
        const lines = content.split('\n');
        const mask = CONFIG_FILE_RE.test(path); // mask secret values from config files in the hits
        let fileHit = false;
        for (let i = 0; i < lines.length; i++) {
          if (!re.test(lines[i])) continue;
          fileHit = true;
          if (filesOnly) break;
          const lo = Math.max(0, i - ctxN), hi = Math.min(lines.length - 1, i + ctxN);
          for (let j = lo; j <= hi; j++) out.push(`${path}:${j + 1}: ${mask ? redactSecrets(lines[j]) : lines[j]}`);
        }
        if (fileHit) matched.push(path);
      }
      const note = (skipped ? `\n[skipped ${skipped} unreadable file${skipped === 1 ? '' : 's'}]` : '') + walked.note;
      if (filesOnly) return (matched.length ? matched.join('\n') : '(no matches)') + note;
      return (out.length ? out.join('\n') : '(no matches)') + note;
    },
  };
}

/** Default instances — cwd-rooted, standard prunes. Hosts that need other roots build their own. */
export const globTool: AgentTool = makeGlobTool();
export const grepTool: AgentTool = makeGrepTool();

/** A line is a top-level declaration worth showing in the repo map. */
const SIG_RE = /^\s*(export\b|(?:export\s+)?(?:async\s+)?function\s+\*?\w|(?:export\s+)?(?:abstract\s+)?class\s+\w|(?:export\s+)?interface\s+\w|(?:export\s+)?type\s+\w|(?:export\s+)?enum\s+\w)/;
const isCode = (p: string) => /\.(ts|tsx|js|jsx|mjs|cjs)$/.test(p);
const isDoc = (p: string) => /\.(md|mdx|txt)$/.test(p);

/** Extract heading hierarchy + first-paragraph summaries from a markdown file. */
function docOutlineOf(content: string, cap = 20): string[] {
  const out: string[] = [];
  const lines = content.split('\n');
  for (let i = 0; i < lines.length && out.length < cap; i++) {
    const hm = lines[i].match(/^(#{1,4})\s+(.+)/);
    if (hm) {
      out.push(hm[0].slice(0, 120));
      for (let j = i + 1; j < lines.length; j++) {
        const l = lines[j].trim();
        if (!l) continue;
        if (l.startsWith('#')) break;
        out.push('  ' + l.slice(0, 120));
        break;
      }
    }
  }
  return out;
}

/** One file's exported/top-level signatures (no bodies), capped. */
function signaturesOf(content: string, cap = 40): string[] {
  const out: string[] = [];
  for (const line of content.split('\n')) {
    if (!SIG_RE.test(line)) continue;
    let sig = line.trim();
    const brace = sig.indexOf('{');
    if (brace > 0) sig = sig.slice(0, brace).trim(); // drop the body
    sig = sig.replace(/\s*=>?\s*$/, '').replace(/=\s*$/, '').slice(0, 120);
    if (sig && !out.includes(sig)) out.push(sig);
    if (out.length >= cap) break;
  }
  return out;
}

/**
 * Compact map of a VFS — code signatures and/or doc outlines. Edge-safe (pure IFilesystem walk).
 * `mode`: "code" (default) = top-level signatures; "docs" = heading outlines; "all" = both.
 */
export async function repoIndex(fs: IFilesystem, glob?: string, mode: 'code' | 'docs' | 'all' = 'code', signal?: AbortSignal): Promise<string> {
  const scope = glob ? anchoredGlob(fs, String(glob)) : null;
  const filter = mode === 'code' ? isCode : mode === 'docs' ? isDoc : (p: string) => isCode(p) || isDoc(p);
  const files = (await walkFiles(fs, fsCwd(fs), signal)).filter((p) => (scope ? scope.test(p) : filter(p)));
  const blocks: string[] = [];
  let shown = 0;
  for (const path of files) {
    ckAbort(signal);
    let content: string;
    try { content = await fs.readFile(path); } catch { continue; }
    const entries = isDoc(path) ? docOutlineOf(content) : signaturesOf(content);
    if (entries.length) { blocks.push(`${path}\n${entries.map((s) => '  ' + s).join('\n')}`); shown += entries.length; }
    if (shown >= 400) { blocks.push('… (map truncated; narrow with `glob`)'); break; }
  }
  const label = mode === 'code' ? 'code signatures' : mode === 'docs' ? 'document outlines' : 'entries';
  return blocks.length ? blocks.join('\n') : `(no ${label} found)`;
}

/** Compact map of the codebase or document workspace — orient in ONE call, not many. */
export const repoMapTool: AgentTool = {
  name: 'RepoMap',
  description:
    'Get a compact map of the workspace: code signatures and/or document outlines in ONE call. `scope`: "code" (default) = functions/classes/types; "docs" = markdown headings + summaries; "all" = both. Call once to orient before diving into specific files — avoids many exploratory Glob/Read calls.',
  parameters: {
    type: 'object',
    properties: {
      glob: { type: 'string', description: 'optional file glob to scope (default: all matching files)' },
      scope: { type: 'string', enum: ['code', 'docs', 'all'], description: 'what to map: "code" (default), "docs", or "all"' },
    },
  },
  run: ({ glob, scope }, ctx) => repoIndex(ctx.fs, glob, scope || 'code', ctx.signal),
};

/**
 * Whitespace-tolerant fallback for Edit: locate `oldStr` in `content` ignoring each line's
 * leading/trailing whitespace, and replace the UNIQUE matching region with `newStr`. Returns
 * null if there isn't exactly one match (caller then errors, forcing a re-read) — so it never
 * guesses. This kills the common re-Read+retry cascade when an exact Edit fails on indentation drift.
 */
export function fuzzyLineReplace(content: string, oldStr: string, newStr: string): string | null {
  const norm = (s: string) => s.trim();
  const cl = content.split('\n');
  const ol = oldStr.split('\n').map(norm);
  while (ol.length && ol[ol.length - 1] === '') ol.pop();
  while (ol.length && ol[0] === '') ol.shift();
  if (!ol.length) return null;
  const matches: number[] = [];
  for (let i = 0; i + ol.length <= cl.length; i++) {
    let ok = true;
    for (let j = 0; j < ol.length; j++) if (norm(cl[i + j]) !== ol[j]) { ok = false; break; }
    if (ok) matches.push(i);
  }
  if (matches.length !== 1) return null; // not found, or ambiguous → don't guess
  const i = matches[0];
  return [...cl.slice(0, i), ...newStr.split('\n'), ...cl.slice(i + ol.length)].join('\n');
}

/** Read-before-overwrite guard, shared by Write and ApplyEdits' whole-file branch: blindly replacing
 *  an existing file the agent never Read destroys its unseen content (the taskify-board incident).
 *  `key` is the resolved path (the readState key); a fresh Write arms readState, so re-writing your own
 *  output passes. Returns true when the write would clobber un-Read content and should be refused. */
async function wouldClobberUnread(ctx: ToolContext, path: string, key: string): Promise<boolean> {
  return !ctx.readState.has(key) && (await ctx.fs.exists(path));
}

/** Create or overwrite a file, creating parent directories as needed (mkdir -p). */
export const writeTool: AgentTool = {
  name: 'Write',
  description:
    'Create or overwrite a file with the given contents, creating parent directories as needed. Use for new files instead of `bash echo >`. To replace an EXISTING file you must Read it first — overwriting an un-Read file is refused (it would destroy unseen content).',
  parameters: {
    type: 'object',
    required: ['path', 'content'],
    properties: { path: { type: 'string' }, content: { type: 'string' } },
  },
  async run({ path, content, contents }, ctx) {
    // Cursor-trained models (composer) call this `contents`. Defaulting a missing body to '' wrote a
    // 0-byte file and reported "Wrote" — on 2026-09-24 that silently emptied a scout's task file.
    const raw = content ?? contents;
    if (raw == null) throw new Error(`Write needs \`content\` (the file body) — none was given for ${path}; nothing written.`);
    const body = String(raw);
    const key = ctx.fs.resolvePath(path);
    if (await wouldClobberUnread(ctx, path, key))
      throw new Error(`Refusing to overwrite ${path}: it exists and hasn't been Read. Read it first, then Write to replace it (or Edit to change part of it).`);
    if (ctx.lint) { const err = ctx.lint(path, body); if (err) throw new Error(err); }
    await mkdirp(ctx.fs, parentDir(key));
    await ctx.fs.writeFile(path, body);
    ctx.readState.set(key, body); // arm Edit on a freshly written file
    return `Wrote ${path}`;
  },
};

/** Apply an ordered list of exact-substring edits to one file in a single call. */
export const multiEditTool: AgentTool = {
  name: 'MultiEdit',
  description:
    'Apply several exact-substring replacements to one file in order, in a single call. Requires a prior Read. Each `old_string` must be unique at the time it is applied. All-or-nothing: if any edit fails, none are written.',
  parameters: {
    type: 'object',
    required: ['path', 'edits'],
    properties: {
      path: { type: 'string' },
      edits: {
        type: 'array',
        items: {
          type: 'object',
          required: ['old_string', 'new_string'],
          properties: { old_string: { type: 'string' }, new_string: { type: 'string' } },
        },
      },
    },
  },
  async run({ path, edits }, ctx) {
    const key = ctx.fs.resolvePath(path);
    const snapshot = ctx.readState.get(key);
    if (snapshot == null) throw new Error(`File has not been read yet: ${path}. Read it before editing.`);
    let current = await ctx.fs.readFile(path);
    if (current !== snapshot) throw new Error(`File ${path} changed since it was read (stale). Re-read before editing.`);
    const list = Array.isArray(edits) ? edits : [];
    if (!list.length) throw new Error('edits must be a non-empty array');
    for (const [i, e] of list.entries()) {
      const count = e.old_string === '' ? 0 : current.split(e.old_string).length - 1;
      if (count === 0) throw new Error(`edit ${i}: old_string not found in ${path}.`);
      if (count > 1) throw new Error(`edit ${i}: old_string is not unique in ${path} (${count} matches).`);
      current = current.replace(e.old_string, () => e.new_string);
    }
    if (ctx.lint) { const err = ctx.lint(path, current); if (err) throw new Error(err); }
    await ctx.fs.writeFile(path, current);
    ctx.readState.set(key, current);
    return `Applied ${list.length} edit(s) to ${path}`;
  },
};

/**
 * Cross-file batch edit — the multi-file refactor primitive. One call edits MANY files:
 * each entry with an `old_string` replaces that exact, UNIQUE substring (read fresh + verified,
 * so NO prior Read is needed — locate the sites with Grep, whose output is the text to match);
 * each entry WITHOUT `old_string` writes `new_string` as the whole file (creates it + parent dirs).
 * Validate-all-before-write → atomic across files. Collapses "Grep + N×(Read+Edit)" into "Grep + ApplyEdits".
 */
export const applyEditsTool: AgentTool = {
  name: 'ApplyEdits',
  description:
    'Apply edits across one or MORE files in a single call — for cross-file refactors (rename/extract/move). edits=[{path, old_string?, new_string}]. WITH old_string: replace that exact substring (must be UNIQUE in the file — add surrounding context; read fresh + verified, no prior Read needed). WITHOUT old_string: write new_string as the whole file (creates it + parent dirs; but to OVERWRITE a file that already exists you must Read it first, else use old_string to edit part of it). Locate sites first with Grep (its output shows the exact text). Atomic: validated across all files before any write.',
  parameters: {
    type: 'object',
    required: ['edits'],
    properties: {
      edits: {
        type: 'array',
        items: {
          type: 'object',
          required: ['path', 'new_string'],
          properties: { path: { type: 'string' }, old_string: { type: 'string' }, new_string: { type: 'string' } },
        },
      },
    },
  },
  async run({ edits }, ctx) {
    const list = Array.isArray(edits) ? edits : [];
    if (!list.length) throw new Error('edits must be a non-empty array of {path, old_string?, new_string}');
    const planned = new Map<string, string>(); // resolved path -> final content (validate ALL before writing → atomic)
    for (const [i, e] of list.entries()) {
      const p = ctx.fs.resolvePath(String(e.path));
      const old = e.old_string == null ? '' : String(e.old_string);
      const neu = String(e.new_string ?? '');
      if (old === '') { // whole-file write / create
        // Same read-before-overwrite guard as Write (skip if an earlier edit in THIS batch already staged p).
        if (!planned.has(p) && (await wouldClobberUnread(ctx, String(e.path), p)))
          throw new Error(`edit ${i}: refusing to overwrite ${e.path} with a whole-file write — it exists and hasn't been Read. Read it first, or pass old_string to edit part of it.`);
        planned.set(p, neu); continue;
      }
      let cur = planned.has(p) ? planned.get(p)! : await ctx.fs.readFile(p).catch(() => { throw new Error(`edit ${i}: file not found: ${e.path}`); });
      const count = cur.split(old).length - 1;
      if (count > 1) throw new Error(`edit ${i}: old_string is not unique in ${e.path} (${count} matches) — add more context`);
      if (count === 1) cur = cur.replace(old, () => neu);
      else {
        const fz = fuzzyLineReplace(cur, old, neu);
        if (fz == null) throw new Error(`edit ${i}: old_string not found in ${e.path}`);
        cur = fz;
      }
      planned.set(p, cur);
    }
    if (ctx.lint) for (const [p, content] of planned) { const err = ctx.lint(p, content); if (err) throw new Error(err); } // validate ALL before any write
    for (const [p, content] of planned) { await mkdirp(ctx.fs, parentDir(p)); await ctx.fs.writeFile(p, content); ctx.readState.set(p, content); }
    return `Applied ${list.length} edit(s) across ${planned.size} file(s): ${[...planned.keys()].join(', ')}`;
  },
};

function parentDir(abs: string): string {
  const i = abs.lastIndexOf('/');
  return i <= 0 ? '/' : abs.slice(0, i);
}

/** mkdir -p over the VFS (idempotent, top-down). */
export async function mkdirp(fs: IFilesystem, dir: string): Promise<void> {
  if (dir === '/' || (await fs.exists(dir))) return;
  await mkdirp(fs, parentDir(dir));
  if (!(await fs.exists(dir))) await fs.createDir(dir);
}

/**
 * Review — verification WITHOUT execution: a fresh-context, adversarial critic pass over the
 * changes the agent just made. Verification-as-review (stolen from the review-fix-commit skill):
 * for an agent with no test runner, "re-read to be sure" is weak, but a COLD reviewer that reads
 * only {task + the produced files} (not the author's chain-of-thought) catches missed edge cases,
 * unstated implications, and subtle logic bugs a happy-path one-shot misses. Costs one model call.
 *
 * The `notes` arg is the with/without-CONTEXT knob: omit it for a pure cold review (the default,
 * least biased); pass it to feed the reviewer extra context. We don't decide which is better —
 * the self-evolution loop discovers it via its prompt rules.
 *
 * Degrades to a no-op notice if no model handle is wired (ctx.ai/ctx.model).
 */
export function reviewTool(): AgentTool {
  return {
    name: 'Review',
    description:
      'Critically review your changes before finishing (verification without running code). Pass the task and the paths you changed; a fresh-context senior reviewer reads them COLD and returns concrete issues or "LGTM". Fix what it raises, then finish. Optional `notes` feeds the reviewer extra context.',
    parameters: {
      type: 'object',
      required: ['task', 'paths'],
      properties: {
        task: { type: 'string', description: 'what was asked — the spec/requirements to check the changes against' },
        paths: { type: 'array', items: { type: 'string' }, description: 'the files you changed/created, to be reviewed' },
        notes: { type: 'string', description: 'OPTIONAL extra context for the reviewer (rationale, constraints). Omit for a pure cold review.' },
      },
    },
    async run({ task, paths, notes }, ctx: ToolContext) {
      if (!ctx.ai || !ctx.model) return '[Review] no model handle wired — skipped.';
      const list: string[] = Array.isArray(paths) ? paths.map(String) : [];
      if (!list.length) return 'Error: pass the paths you changed in `paths`.';
      const files: string[] = [];
      for (const p of list.slice(0, 12)) {
        try {
          const body = await ctx.fs.readFile(p);
          files.push(`--- ${p} ---\n${body.length > 4000 ? body.slice(0, 4000) + '\n…(truncated)' : body}`);
        } catch {
          files.push(`--- ${p} ---\n[could not read]`);
        }
      }
      const prompt =
        'You are a senior engineer doing a critical code review. Review the changes below AGAINST THE TASK with deep, skeptical thinking — default to finding problems.\n' +
        'Focus on: correctness, edge cases (empty/boundary/negative inputs), unstated-but-implied requirements, and subtle logic bugs. Do NOT comment on style.\n\n' +
        `TASK:\n${String(task ?? '').trim()}\n\n` +
        (notes ? `CONTEXT FROM THE AUTHOR:\n${String(notes).trim()}\n\n` : '') +
        `CHANGED FILES:\n${files.join('\n\n')}\n\n` +
        'Reply with a short numbered list of concrete, actionable issues. If the changes correctly and completely satisfy the task with no edge cases missed, reply with exactly: LGTM';
      try {
        const r = (await ctx.ai.chat({ model: ctx.model, messages: [{ role: 'user', content: prompt }], stream: false })) as ChatResponse;
        const text = (r?.content ?? '').trim();
        return text || 'LGTM';
      } catch (e: any) {
        return `[Review] model error: ${e?.message ?? e} — skipped.`;
      }
    },
  };
}
